import React, { useState } from "react";
import { auth } from "../firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  GithubAuthProvider,
  sendPasswordResetEmail,
  updateProfile,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { User, LogIn, UserPlus } from "lucide-react";
import { FaGithub, FaGoogle } from "react-icons/fa";

export default function Account({ theme }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");

  
  // Separate state for register and login
  const [registerForm, setRegisterForm] = useState({
    username: "",
    email: "",
    email2: "",
    password: "",
    password2: "",
  });
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
    remember: false,
  });

  const [showSetUsername, setShowSetUsername] = useState(false);
  const [socialUser, setSocialUser] = useState(null);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  // Providers
  const googleProvider = new GoogleAuthProvider();
  const githubProvider = new GithubAuthProvider();

  // THEME: Safe fallback
  const safeTheme = theme || {
    colors: {
      background: "#23252b",
      text: "#fff",
      logoBackground: "#444",
      logoText: "#fff",
      inputBackground: "#181920",
      inputText: "#fff",
      textSecondary: "#aaa",
      error: "#f87171",
      success: "#34d399",
      border: "#333",
    },
    fontFamily: "inherit",
  };

  // Theme helpers
  const boxStyle = {
    background: safeTheme.colors.background,
    color: safeTheme.colors.text,
    border: `1.5px solid ${safeTheme.colors.logoBackground}`,
    fontFamily: safeTheme.fontFamily,
    boxShadow: "0 4px 18px rgba(0,0,0,0.09)",
    transition: "background 0.2s, color 0.2s",
  };
  const inputStyle = {
    background: safeTheme.colors.inputBackground ?? safeTheme.colors.background,
    color: safeTheme.colors.inputText ?? safeTheme.colors.text,
    border: `1.5px solid ${safeTheme.colors.logoBackground}`,
    fontFamily: safeTheme.fontFamily,
    outline: "none",
    transition: "background 0.18s, border 0.2s",
  };
  const buttonStyle = {
    background: safeTheme.colors.logoBackground,
    color: safeTheme.colors.logoText,
    border: `1.5px solid ${safeTheme.colors.logoBackground}`,
    fontFamily: safeTheme.fontFamily,
    fontWeight: 700,
    transition: "background 0.2s, color 0.2s",
  };
  const buttonHoverStyle = {
    background: safeTheme.colors.logoText,
    color: safeTheme.colors.logoBackground,
    border: `1.5px solid ${safeTheme.colors.logoBackground}`,
  };
  const secondaryText = { color: safeTheme.colors.textSecondary ?? "#aaa" };

  // Handlers
  function handleRegisterInput(e) {
    setRegisterForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }
  function handleLoginInput(e) {
    setLoginForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleRegister(e) {
    e.preventDefault();
    setErr("");
    if (!registerForm.username.trim()) return setErr("Username required.");
    if (registerForm.email !== registerForm.email2) return setErr("Emails do not match.");
    if (registerForm.password !== registerForm.password2) return setErr("Passwords do not match.");
    try {
      setLoading(true);
      const cred = await createUserWithEmailAndPassword(auth, registerForm.email, registerForm.password);
      await updateProfile(cred.user, { displayName: registerForm.username });
      setInfo("Account created! You can log in.");
      setMode("login");
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }

  async function handleLogin(e) {
    e.preventDefault();
    setErr("");
    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, loginForm.email, loginForm.password);
      navigate("/");
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }

  async function handleProvider(provider) {
    setErr("");
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, provider);
      if (!result.user.displayName) {
        setShowSetUsername(true);
        setSocialUser(result.user);
      } else {
        navigate("/");
      }
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }

  async function handleSetSocialUsername(e) {
    e.preventDefault();
    if (!registerForm.username.trim()) return setErr("Username required.");
    try {
      setLoading(true);
      await updateProfile(socialUser, { displayName: registerForm.username });
      navigate("/");
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }

  async function handleForgot() {
    if (!loginForm.email) return setErr("Enter your email.");
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, loginForm.email);
      setInfo("Reset email sent!");
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }

  // --- UI ---

  if (showSetUsername) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{
          background: safeTheme.colors.background,
          color: safeTheme.colors.text,
          fontFamily: safeTheme.fontFamily,
        }}
      >
        <form
          onSubmit={handleSetSocialUsername}
          className="flex flex-col items-center"
        >
          <div className="flex items-center gap-2 mb-2 text-xl">
            <User size={24} />
            <span className="font-bold">Set username</span>
          </div>
          <input
            name="username"
            value={registerForm.username}
            onChange={handleRegisterInput}
            placeholder="username"
            className="px-3 py-2 rounded"
            style={inputStyle}
          />
          <button
            className="mt-3 px-4 py-2 rounded font-bold"
            style={buttonStyle}
            type="submit"
            disabled={loading}
            onMouseOver={e => Object.assign(e.target.style, buttonHoverStyle)}
            onMouseOut={e => Object.assign(e.target.style, buttonStyle)}
          >
            Save
          </button>
          {err && <div style={{ color: safeTheme.colors.error, minHeight: 22 }}>{err}</div>}
        </form>
      </div>
    );
  }
  
  return (
    <div
    className="flex items-center justify-center min-h-screen gap-16 font-mono"
    style={{
      background: safeTheme.colors.background,
      color: safeTheme.colors.text,
      fontFamily: safeTheme.fontFamily,
    }}
  >  
      {/* REGISTER */}
      <form
        className="rounded-xl p-8 w-[340px] flex flex-col gap-2 shadow"
        style={{ ...boxStyle, opacity: mode === "register" ? 1 : 0.75 }}
        onSubmit={handleRegister}
        onClick={() => setMode("register")}
      >
        <div className="flex items-center gap-2 mb-2 text-xl" style={{ color: safeTheme.colors.text }}>
          <UserPlus size={24} />
          <span className="font-bold">Register</span>
        </div>
        <input name="username" value={registerForm.username} onChange={handleRegisterInput}
          placeholder="username" className="px-3 py-2 rounded mb-2" style={inputStyle} />
        <input name="email" value={registerForm.email} onChange={handleRegisterInput}
          placeholder="email" type="email" className="px-3 py-2 rounded" style={inputStyle} />
        <input name="email2" value={registerForm.email2} onChange={handleRegisterInput}
          placeholder="verify email" type="email" className="px-3 py-2 rounded" style={inputStyle} />
        <input name="password" value={registerForm.password} onChange={handleRegisterInput}
          placeholder="password" type="password" className="px-3 py-2 rounded" style={inputStyle} />
        <input name="password2" value={registerForm.password2} onChange={handleRegisterInput}
          placeholder="verify password" type="password" className="px-3 py-2 rounded" style={inputStyle} />
        <button
          className="mt-3 px-4 py-2 rounded font-bold"
          style={buttonStyle}
          type="submit"
          disabled={loading || mode !== "register"}
          onMouseOver={e => Object.assign(e.target.style, buttonHoverStyle)}
          onMouseOut={e => Object.assign(e.target.style, buttonStyle)}
        >
          <UserPlus size={18} className="inline-block mr-1" /> Sign up
        </button>
      </form>

      {/* LOGIN */}
      <form
        className="rounded-xl p-8 w-[340px] flex flex-col gap-2 shadow"
        style={{ ...boxStyle, opacity: mode === "login" ? 1 : 0.75 }}
        onSubmit={handleLogin}
        onClick={() => setMode("login")}
      >
        <div className="flex items-center gap-2 mb-2 text-xl" style={{ color: safeTheme.colors.text }}>
          <LogIn size={24} />
          <span className="font-bold">Login</span>
        </div>
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded text-xl"
            style={inputStyle}
            onClick={() => handleProvider(googleProvider)}
          >
            <FaGoogle size={22} className="mr-2" style={{ color: "#EA4335" }} />
            <span>Google</span>
          </button>
          <button
            type="button"
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded text-xl"
            style={inputStyle}
            onClick={() => handleProvider(githubProvider)}
          >
            <FaGithub size={22} className="mr-2" />
            <span>GitHub</span>
          </button>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <div className="h-px flex-1" style={{ background: safeTheme.colors.logoBackground }} />
          <span className="text-sm" style={secondaryText}>or</span>
          <div className="h-px flex-1" style={{ background: safeTheme.colors.logoBackground }} />
        </div>
        <input name="email" value={loginForm.email} onChange={handleLoginInput}
          placeholder="email" className="px-3 py-2 rounded" style={inputStyle} />
        <input name="password" value={loginForm.password} onChange={handleLoginInput}
          placeholder="password" type="password" className="px-3 py-2 rounded" style={inputStyle} />
        <div className="flex items-center gap-2 mt-1 mb-2">
        </div>
        <button
          className="mt-2 px-4 py-2 rounded font-bold"
          style={buttonStyle}
          type="submit"
          disabled={loading || mode !== "login"}
          onMouseOver={e => Object.assign(e.target.style, buttonHoverStyle)}
          onMouseOut={e => Object.assign(e.target.style, buttonStyle)}
        >
          <LogIn size={18} className="inline-block mr-1" /> Sign in
        </button>
        <div className="mt-1 text-right">
          <button type="button" className="text-sm underline" style={secondaryText} onClick={handleForgot}>forgot password?</button>
        </div>
      </form>
      {/* Error/info */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-lg" style={{ color: safeTheme.colors.error, minHeight: 28 }}>{err}</div>
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-lg" style={{ color: safeTheme.colors.success, minHeight: 22 }}>{info}</div>
    </div>
  );
}

